import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { bookkeepingReviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

// Add these when you have examples ready
// const image1 = `${process.env.PUBLIC_URL}/assets/bookkeepingExample1.png`;
// const image2 = `${process.env.PUBLIC_URL}/assets/bookkeepingExample2.png`;
// const image3 = `${process.env.PUBLIC_URL}/assets/bookkeepingExample3.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/Logo.svg`;

function ImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!Array.isArray(images)) return;

    images.forEach((img) => {
      const preloader = new Image();
      preloader.src = img.image;
    });
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Paper className="bws-gsl-carousel" elevation={0}>
      <div className="bws-gsl-carouselInner">
        <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
          <ChevronLeftIcon />
        </IconButton>

        <div className="bws-gsl-carouselFrame">
          {images.map((img, i) => (
            <img
              key={i}
              className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
              src={img.image}
              alt={img.altText}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>

        <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="bws-gsl-carouselDots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`View example ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </Paper>
  );
}

export default function Bookkeeping() {
  const safeReviews = Array.isArray(bookkeepingReviews) ? bookkeepingReviews : [];
  const title = "Bookkeeping Services — Cleanup, Reports, Ongoing Support";
  const description =
    "Bookkeeping services for small businesses and individuals, including cleanup, organized records, summaries, reporting, and ongoing support.";
  const canonical = "https://brainwavesolutions.net/services/bookkeeping";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "Bookkeeping", url: canonical },
    ]),
    buildServiceSchema({
      name: "Bookkeeping",
      description,
      canonical,
      offerLabel: "$75–$750+",
      serviceType: "Bookkeeping support",
    }),
  ];

  const images = [
    // {
    //   image: image1,
    //   altText:
    //     "Bookkeeping summary spreadsheet showing categorized expenses, monthly totals, and organized business records.",
    // },
    // {
    //   image: image2,
    //   altText:
    //     "Monthly bookkeeping report with income, expenses, category breakdowns, and cash flow summary.",
    // },
    // {
    //   image: image3,
    //   altText:
    //     "Clean bookkeeping worksheet prepared for reporting with organized categories and reconciled transaction data.",
    // },
  ].filter((img) => img.image);

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                Bookkeeping services that help keep your records clean, organized, and easier to work with
              </Typography>

              <p className="bws-gsl-lead">
                When bookkeeping starts falling behind, everything gets harder. Reports take longer, categories get messy,
                records stop feeling trustworthy, and even simple questions about income, expenses, or cash flow become
                more stressful than they should be.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help businesses and individuals clean up records, organize bookkeeping data, prepare summaries and
                reports, and keep things moving on either a one-time or ongoing basis. If needed, I can also build
                tools or simple automation to make the work easier over time — but the main goal here is dependable
                bookkeeping support.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">Bookkeeping cleanup</span>
                <span className="bws-gsl-badge">Monthly summaries</span>
                <span className="bws-gsl-badge">Ongoing support</span>
                <span className="bws-gsl-badge">Reporting prep</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Get bookkeeping help
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses &amp; individuals</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical support</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common bookkeeping problems I help with</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Books that are behind or need cleanup</li>
                  <li>Messy categories and inconsistent records</li>
                  <li>Monthly summaries that take too long to prepare</li>
                  <li>Reports and rollups that need to be more organized</li>
                  <li>Ongoing bookkeeping support to keep things current</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message describing what needs to be cleaned up, maintained, or reported, and whether you need one-time or ongoing help.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Get organized again</Typography>
              <Typography className="bws-gsl-stripText">
                Clean up records, categories, and supporting data so the books are easier to trust.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Make reporting easier</Typography>
              <Typography className="bws-gsl-stripText">
                Prepare summaries, rollups, and bookkeeping reports without the usual scramble.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Keep things moving</Typography>
              <Typography className="bws-gsl-stripText">
                Get one-time cleanup or ongoing support to keep records current and usable.
              </Typography>
            </Paper>
          </div>

          {images.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Examples:</Typography>
                <p className="bws-gsl-p">
                  Examples of organized bookkeeping records, summaries, and reporting support.
                </p>
              </div>

              <ImageCarousel images={images} />
            </div>
          )}

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>Bookkeeping cleanup and catch-up work</li>
                    <li>Category mapping and record organization</li>
                    <li>Monthly summaries and bookkeeping reports</li>
                    <li>Ongoing bookkeeping support</li>
                    <li>Optional tools and automation to simplify the workflow</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Small businesses that need cleaner books</li>
                    <li>Owners who need help staying current</li>
                    <li>People who want clearer summaries and reports</li>
                    <li>Anyone who needs practical bookkeeping help without unnecessary complexity</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {safeReviews.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Client feedback</Typography>
              </div>

              <div className="bws-gsl-reviews">
                {safeReviews.slice(0, 3).map((r, idx) => (
                  <Paper key={idx} className="bws-gsl-review" elevation={0}>
                    <Rating
                      value={5}
                      readOnly
                      size="small"
                      className="bws-gsl-reviewStars"
                      aria-label="5 out of 5 stars"
                    />
                    <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                    <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                  </Paper>
                ))}
              </div>
            </div>
          )}

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Do you offer one-time cleanup as well as ongoing help?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. Some clients need a cleanup and reset, while others want ongoing support to keep things current.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you also help make the bookkeeping process easier?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. If needed, I can help improve the workflow with better spreadsheets, reports, or lightweight automation.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of the current bookkeeping situation, what needs to be cleaned up or maintained, and whether you are looking for one-time or ongoing help.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need bookkeeping help?</Typography>
              <p className="bws-muted">
                Send a quick description of what needs to be cleaned up, reported, or kept current.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
